// @flow

import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { compose, filter, isEmpty, map, pipe, prop, sortBy } from "ramda";

import {
  GRUPAMENTO_MEDIDAS_QUALITATIVAS,
  MODO_APP_EXCEL,
  nomeCorresponde,
  type MedidaTraduzida,
} from "../../model";
import {
  adicionarMedidaQualitativa,
  getMedidasAgrupadas,
  selecionarMedida,
  selecionarFiltrosTipoAtivo,
  selecionarGrupamentoMedida,
  setSeletorAberto,
  getModoApp,
} from "../../state";

import { normalizarString } from "../../util";

import { useI18n } from "@quantum/i18n";
import { ClickOutside, SearchIcon } from "@quantum/ui";
import { FaAngleDown, FaAngleUp } from "react-icons/fa";

import { modosExibicao } from "../edicaoMedidas/modosExibicao/ModoExibicao";

import "./busca-global.css";
import { useSelectState } from "../comum/SelectInput";
import { useRegisterFocusArea } from "../focus-area";

const BuscaGlobal = () => {
  const { translate } = useI18n();
  const dispatch = useDispatch();

  const inputRef = useRegisterFocusArea("inputBuscaGlobalResultados");

  const [query, setQuery] = useState("");
  const expandido = useSelectState("buscaGlobal");
  const [isMouseInside, setIsMouseInside] = useState(false);

  const medidasAgrupadas: { [string]: Array<MedidaTraduzida> } = useSelector(
    getMedidasAgrupadas
  );
  const medidasAgrupadasFiltradas = useMemo(
    () =>
      pipe(
        map(
          filter(
            ({ nome, traducao }) =>
              nomeCorresponde(query, nome) || nomeCorresponde(query, traducao)
          )
        ),
        map(sortBy(compose(normalizarString, prop("traducao"))))
      )(medidasAgrupadas),
    [medidasAgrupadas, query]
  );

  const nomesGrupamento = useMemo(
    () => ({
      QUANTITATIVA: translate("maiusculas.medidasQuantitativas"),
      QUALITATIVA: translate("maiusculas.medidasQualitativas"),
      SERIES: translate("maiusculas.medidasSeries"),
      EMPRESAS: translate("maiusculas.medidasEmpresas"),
    }),
    [translate]
  );

  const onChangeQuery = useCallback(event => setQuery(event.target.value), []);

  const onClickExpandir = useCallback(
    event => {
      dispatch(setSeletorAberto("buscaGlobal", expandido.open));
    },
    [dispatch, expandido.open]
  );

  const onClickMedida = useCallback(
    (medida: MedidaTraduzida) => () => {
      switch (medida.grupamentoMedida.nome) {
        case GRUPAMENTO_MEDIDAS_QUALITATIVAS: {
          dispatch(
            adicionarMedidaQualitativa(
              medida.idConfiguracaoRelatorio,
              modosExibicao.MEDIDA_QUALITATIVA.geradorMedidasSelecionadas,
              modosExibicao.MEDIDA_QUALITATIVA.actionCreatorAdicionar
            )
          );
          break;
        }
        default: {
          dispatch(selecionarGrupamentoMedida(medida.grupamentoMedida.nome));
          dispatch(selecionarMedida(medida.idConfiguracaoRelatorio));
        }
      }

      dispatch(selecionarFiltrosTipoAtivo(medida.filtros));
      setQuery("");
      dispatch(setSeletorAberto("buscaGlobal", false));
    },
    [dispatch]
  );

  const onFocus = useCallback(
    () => dispatch(setSeletorAberto("buscaGlobal", true)),
    [dispatch]
  );
  const onBlur = useCallback(
    () => dispatch(setSeletorAberto("buscaGlobal", false)),
    [dispatch]
  );

  return (
    <ClickOutside>
      <div className="BuscaGlobal" onBlur={() => !isMouseInside && onBlur()}>
        <SearchIcon />
        <input
          ref={
            useSelector(getModoApp) === MODO_APP_EXCEL ? inputRef() : inputRef
          }
          value={query}
          className="BuscaGlobal__texto"
          type="text"
          placeholder={translate(
            "configuracaoRelatorio.placeholderBuscaGlobalMedidas"
          )}
          onChange={onChangeQuery}
          onFocus={onFocus}
        />
        <div className="BuscaGlobal__arrow" onClick={onClickExpandir}>
          {expandido.open ? <FaAngleUp /> : <FaAngleDown />}
        </div>

        {expandido.open && (
          <div
            onMouseEnter={() => setIsMouseInside(true)}
            onMouseLeave={() => setIsMouseInside(false)}
            className="BuscaGlobal__resultados"
          >
            {["QUANTITATIVA", "QUALITATIVA", "SERIES", "EMPRESAS"]
              .filter(
                grupamento =>
                  medidasAgrupadasFiltradas[grupamento] != null &&
                  !isEmpty(medidasAgrupadasFiltradas[grupamento])
              )
              .map(grupamento => ({
                nome: nomesGrupamento[grupamento],
                medidas: medidasAgrupadasFiltradas[grupamento],
              }))
              .map(({ nome, medidas }) => (
                <div key={nome} className="BuscaGlobal__grupo-resultado">
                  <label className="BuscaGlobal__nome-grupo">{nome}</label>
                  {medidas.map((medida, index) => (
                    <div
                      key={index}
                      onClick={onClickMedida(medida)}
                      className="BuscaGlobal__resultado"
                    >
                      {medida.traducao}
                    </div>
                  ))}
                </div>
              ))}
          </div>
        )}
      </div>
    </ClickOutside>
  );
};

export default BuscaGlobal;
